import { Suspense, lazy } from 'react';
import { Navigate } from 'react-router-dom';
import { RouteObject } from 'react-router';

import SidebarLayout from 'src/layouts/SidebarLayout';
import SuspenseLoader from 'src/components/SuspenseLoader';
import { ProtectedRoute } from './components/ProtectedRoute';

import { MAINTENANCE_MODE } from './config';

// MAINTENANCE MODE - Can be controlled via REACT_APP_MAINTENANCE_MODE environment variable

import ViewImages from './content/pages/Images/View/view';
import { ViewImage } from './content/pages/Images/View/single';
import { UserProvider } from './components/ProtectedRoute/userProvider';
import HomePage from './content/pages/Home/index';
import AboutPage from './content/pages/About/index';
import PersonalDetailsForm from './content/pages/User/PersonalDetails';
import Dashboard from './content/pages/User/Dashboard';
import StripeReturnPage from './content/pages/Stripe/return';
import StripeRefreshPage from './content/pages/Stripe/refresh';
import StripeSetupPage from './content/pages/Stripe/setup';
import FeedbackForm from './content/pages/Feedback/FeedbackForm';
import AddImage from './content/pages/Images/AddSingle/AddImage';
import CategoriesPage from './content/pages/Home/categories';
import TermsAndConditions from './content/pages/Terms';
import PayReturn from './content/pages/Images/View/payreturn';
import SingleCompetitionPage from './content/pages/Competitions/single';
import CompetitionsListPage from './content/pages/Competitions/list';
import EnterCompetitionPage from './content/pages/Competitions/enter';
import BrowseImagesPage from './content/pages/Images/Browse';


const Loader = (Component) => (props) =>
  (
    <Suspense fallback={<SuspenseLoader />}>
      <Component {...props} />
    </Suspense>
  );

// Status
const Status404 = Loader(
  lazy(() => import('src/content/pages/Status/Status404'))
);
const Status500 = Loader(
  lazy(() => import('src/content/pages/Status/Status500'))
);
const StatusComingSoon = Loader(
  lazy(() => import('src/content/pages/Status/ComingSoon'))
);
const StatusMaintenance = Loader(
  lazy(() => import('src/content/pages/Status/Maintenance'))
);

const routes: RouteObject[] = [
  // Maintenance mode - redirect all traffic to maintenance page
  ...(MAINTENANCE_MODE ? [{
    path: '*',
    element: <Navigate to="/status/maintenance" replace />
  }] : []),
  {
    path: '/',
    element: (
      <UserProvider>
        <SidebarLayout />
      </UserProvider>
    ),
    children: [
      {
        path: '/',
        element: <HomePage />
      },
      {
        path: 'about',
        element: <AboutPage />
      },
      {
        path: 'terms',
        element: <TermsAndConditions />
      },
      {
        path: 'categories',
        element: <CategoriesPage />
      },
      {
        path: 'status',
        children: [
          { path: '', element: <Navigate to="404" replace /> },
          { path: '404', element: <Status404 /> },
          { path: '500', element: <Status500 /> },
          { path: 'maintenance', element: <StatusMaintenance /> },
          { path: 'coming-soon', element: <StatusComingSoon /> }
        ]
      },
      // New Competitions route
      {
        path: 'competitions',
        children: [
          { path: '', element: <CompetitionsListPage /> },
          { path: 'view', element: <SingleCompetitionPage /> },
          { path: 'enter', element: <EnterCompetitionPage /> }
        ]
      },
      {
        path: 'browse',
        element: <BrowseImagesPage />
      },
      { path: '*', element: <Status404 /> }
    ]
  },
  {
    path: '/user/:userId',
    element: (
      <UserProvider>
        <SidebarLayout />
      </UserProvider>
    ),
    children: [
      { path: '', element: <BrowseImagesPage /> }
    ]
  },
  {
    path: '/navigation',
    element: (
      <UserProvider>
        <SidebarLayout />
      </UserProvider>
    ),
    children: [
      { path: 'home', element: <ProtectedRoute><HomePage /></ProtectedRoute> }
    ]
  },
  {
    path: '/accounts',
    element: (
      <UserProvider>
        <SidebarLayout />
      </UserProvider>
    ),
    children: [
      { path: 'profile', element: <ProtectedRoute><PersonalDetailsForm /></ProtectedRoute> },
      { path: 'dashboard', element: <ProtectedRoute><Dashboard /></ProtectedRoute> }
    ]
  },
  {
    path: '/images',
    element: (
      <UserProvider>
        <SidebarLayout />
      </UserProvider>
    ),
    children: [
      { path: 'view/*', element: <ProtectedRoute><ViewImages /></ProtectedRoute> },
      { path: 'addsingle', element: <ProtectedRoute><AddImage /></ProtectedRoute> }
    ]
  },
  {
    path: '/image',
    element: (
      <UserProvider>
        <SidebarLayout />
      </UserProvider>
    ),
    children: [
      {
        path: ':imageId',
        element: <ViewImage />
      },
      {
        path: ':imageId/return',
        element: <PayReturn />
      }
    ]
  },
  {
    path: '/stripe',
    element: (
      <UserProvider>
        <SidebarLayout />
      </UserProvider>
    ),
    children: [
      { path: 'return', element: <ProtectedRoute><StripeReturnPage /></ProtectedRoute> },
      { path: 'refresh', element: <ProtectedRoute><StripeRefreshPage /></ProtectedRoute> },
      { path: 'setup', element: <ProtectedRoute><StripeSetupPage /></ProtectedRoute> }
    ]
  },
  {
    path: '/feedback',
    element: (
      <UserProvider>
        <SidebarLayout />
      </UserProvider>
    ),
    children: [
      { path: '', element: <FeedbackForm /> }
    ]
  }
];

export default routes;
